import type { MouseEvent } from "react";

import styles from "./ChipV2.module.css";

import { IconV2, type IconV2Name } from "@/components/atoms/IconV2";

export type ChipV2Variant =
  | "sun"
  | "label"
  | "alert"
  | "alarm"
  | "ok"
  | "online"
  | "no-disponible"
  | "neutral"
  | "accent";

interface ChipV2Base {
  variant: ChipV2Variant;
  label: string;
  leftIcon?: IconV2Name;
  rightIcon?: IconV2Name;
  ariaLabel?: string;
}

// disabled solo tiene sentido sobre un chip interactivo (con onClick); la unión
// hace que pasarlo a un chip estático sea error de tipo, no un fallo silencioso.
type Props =
  | (ChipV2Base & {
      onClick: (event: MouseEvent<HTMLButtonElement>) => void;
      onRightIconClick?: never;
      disabled?: boolean;
    })
  | (ChipV2Base & {
      onClick?: undefined;
      onRightIconClick?: (event: MouseEvent<HTMLButtonElement>) => void;
      disabled?: never;
    });

export const ChipV2 = ({
  variant,
  label,
  leftIcon,
  rightIcon,
  onClick,
  onRightIconClick,
  ariaLabel,
  disabled,
}: Props) => {
  const content = (
    <>
      {leftIcon !== undefined && (
        <span className={styles.icon}>
          <IconV2 name={leftIcon} size="s" />
        </span>
      )}
      <span className={styles.text}>{label}</span>
      {rightIcon !== undefined &&
        (onRightIconClick ? (
          <button
            type="button"
            className={styles.iconButton}
            onClick={onRightIconClick}
            aria-label={ariaLabel ?? label}
          >
            <IconV2 name={rightIcon} size="s" />
          </button>
        ) : (
          <span className={styles.icon}>
            <IconV2 name={rightIcon} size="s" />
          </span>
        ))}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`${styles.chip} ${styles.clickable}`}
        data-variant={variant}
        onClick={onClick}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        {content}
      </button>
    );
  }

  return (
    <span className={styles.chip} data-variant={variant}>
      {content}
    </span>
  );
};
